import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { catalogApi } from '../lib/api'
import { kvSet } from '../lib/db'
import { useApp } from '../lib/store'
import { AppBar } from '../components/AppBar'

export function ShopSettingsScreen() {
  const nav = useNavigate()
  const shop = useApp((s) => s.shop)
  const say = useApp((s) => s.say)

  const [name, setName] = useState(shop?.name ?? '')
  const [address, setAddress] = useState(shop?.address ?? '')
  const [phone, setPhone] = useState(shop?.phone ?? '')
  const [taxCode, setTaxCode] = useState(shop?.taxCode ?? '')
  const [kind, setKind] = useState<'FnB' | 'Retail'>(shop?.businessType ?? 'FnB')
  // Gõ theo phần trăm vì đó là cách chủ quán nói, lưu theo phần nghìn vì quán thu 3,5%
  // là chuyện có thật. Giữ nguyên chuỗi lúc gõ, đừng ép về số: ép thì gõ "3," là mất
  // luôn dấu phẩy vừa bấm.
  const [feePercent, setFeePercent] = useState(
    shop?.serviceChargePermille ? String(shop.serviceChargePermille / 10).replace('.', ',') : '',
  )
  const [payLater, setPayLater] = useState(shop?.payLater ?? false)
  /*
   * Mã huỷ đơn chỉ ghi vào, không đọc ra được.
   *
   * Máy chủ giữ chuỗi băm nên chính nó cũng không biết mã cũ là gì. Quên thì đặt lại — mất
   * mã còn hơn mất khả năng chứng minh ai đã huỷ cái bill nào.
   */
  const [passcode, setPasscode] = useState('')
  const [dropPass, setDropPass] = useState(false)
  const [busy, setBusy] = useState(false)
  const hasPass = Boolean(shop?.voidPasscodeHash)

  const rate = kind === 'FnB' ? '4,5%' : '1,5%'
  const kindChanged = kind !== shop?.businessType

  const feePermille = Math.round(Number(feePercent.replace(',', '.').trim() || '0') * 10)
  const feeOk = Number.isFinite(feePermille) && feePermille >= 0 && feePermille <= 300

  async function save() {
    if (!name.trim()) {
      say('Chưa đặt tên quán')
      return
    }
    if (!feeOk) {
      say('Phí phục vụ chỉ nhận từ 0% đến 30%')
      return
    }
    if (passcode && passcode.length !== 6) {
      say('Mã huỷ đơn phải đúng 6 chữ số')
      return
    }

    setBusy(true)
    try {
      const saved = await catalogApi.saveShop({
        name: name.trim(),
        address: address.trim() || null,
        phone: phone.trim() || null,
        taxCode: taxCode.trim() || null,
        businessType: kind,
        serviceChargePermille: feePermille,
        payLater,
        // Không gõ gì và không bấm bỏ mã thì đừng gửi ô này lên: gửi bừa một chuỗi rỗng
        // là xoá mất mã quán đang dùng.
        ...(dropPass ? { voidPasscode: '' } : passcode ? { voidPasscode: passcode } : {}),
      })

      const next = {
        ...shop!,
        name: saved.name,
        address: saved.address,
        phone: saved.phone,
        taxCode: saved.taxCode,
        businessType: saved.businessType,
        vatRatePermille: saved.vatRatePermille,
        pitRatePermille: saved.pitRatePermille,
        serviceChargePermille: saved.serviceChargePermille,
        payLater: saved.payLater,
        voidPasscodeHash: saved.voidPasscodeHash,
      }
      await kvSet('shop', next)
      useApp.setState({ shop: next })

      say('Đã lưu thông tin quán')
      nav('/settings')
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setBusy(false)
  }

  return (
    <div className="screen">
      <AppBar title="Thông tin quán" onBack={() => nav('/settings')} />

      <div className="body">
        <p className="hint">
          Tên, địa chỉ và mã số thuế được in lên tờ bill và ghi vào sổ doanh thu. Khai đúng ngay
          từ đầu thì cuối quý khỏi phải sửa lại từng dòng.
        </p>

        <label className="field">
          <span>Tên quán</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Cà phê Sáu Lúa"
            maxLength={120}
          />
        </label>

        <label className="field">
          <span>Địa chỉ</span>
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="142 Nguyễn Thị Minh Khai, P. Đa Kao, Q.1"
            maxLength={250}
          />
        </label>

        <div className="field-row">
          <label className="field">
            <span>Điện thoại</span>
            <input
              type="tel"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0912 345 678"
            />
          </label>

          <label className="field">
            <span>Mã số thuế</span>
            <input
              inputMode="numeric"
              value={taxCode}
              onChange={(e) => setTaxCode(e.target.value)}
              placeholder="0123456789"
              maxLength={50}
            />
          </label>
        </div>

        <label className="field">
          <span>Phí phục vụ</span>
          <div className="field-suffix">
            <input
              inputMode="decimal"
              value={feePercent}
              onChange={(e) => setFeePercent(e.target.value)}
              placeholder="0"
              maxLength={5}
            />
            <span aria-hidden="true">%</span>
          </div>
          <small className="note">
            Cộng thêm vào mỗi hoá đơn, tính trên số tiền sau khi đã giảm giá. Để trống hoặc
            gõ 0 là quán không thu — khi đó không có dòng phí nào hiện ra ở đâu cả.
          </small>
        </label>

        {/*
          Quyết định đúng một việc: thu tiền xong thì ô bàn trống ngay, hay vẫn bận.
          Quán ăn thu lúc khách đứng dậy ra về, còn quán cà phê thu trước rồi khách mới
          ngồi — hai bên ngược nhau hoàn toàn.
        */}
        <div className="field">
          <span>Thu tiền lúc nào</span>
          <div className="seg">
            <button type="button" aria-pressed={!payLater} onClick={() => setPayLater(false)}>
              ☕ Lúc gọi món
            </button>
            <button type="button" aria-pressed={payLater} onClick={() => setPayLater(true)}>
              🍜 Khi khách về
            </button>
          </div>
          <small className="note">
            {payLater
              ? 'Thu tiền xong là khách ra về, nên ô bàn trống lại ngay.'
              : 'Khách trả tiền rồi mới ngồi hoặc đứng đợi, nên ô bàn vẫn bận cho tới khi nhân viên bấm dọn bàn hoặc giao đồ.'}
          </small>
        </div>

        {/*
          Mã 6 số chặn cửa huỷ hoá đơn đã thu tiền.
          Huỷ một bill đã thu là sửa vào sổ doanh thu, nên nó không nên nằm trong tầm tay
          của bất kỳ ai đang cầm máy — kể cả khi người ta chỉ định bấm thử cho biết.
        */}
        <label className="field">
          <span>Mã huỷ đơn đã thu tiền</span>
          <input
            value={dropPass ? '' : passcode}
            onChange={(e) => {
              setDropPass(false)
              setPasscode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }}
            inputMode="numeric"
            autoComplete="off"
            placeholder={hasPass ? 'Gõ 6 số mới để đổi mã' : 'Đặt mã 6 số'}
          />

          {hasPass && (
            <button
              type="button"
              className="btn btn-danger-ghost"
              onClick={() => {
                setPasscode('')
                setDropPass((v) => !v)
              }}
            >
              {dropPass ? 'Thôi, giữ mã cũ' : 'Bỏ mã, không hỏi nữa'}
            </button>
          )}

          <small className="note">
            {dropPass
              ? 'Bấm Lưu là bỏ mã. Sau đó ai cầm máy cũng huỷ được hoá đơn đã thu tiền.'
              : hasPass
                ? 'Quán đang có mã. Không xem lại được mã cũ — gõ 6 số mới là thay mã, để trống là giữ nguyên.'
                : 'Chưa đặt mã, nên hiện giờ ai cũng huỷ được hoá đơn đã thu tiền. Đặt một mã rồi chỉ người biết mã mới huỷ được.'}
          </small>
        </label>

        <div className="field">
          <span>Ngành nghề</span>
          <div className="seg">
            <button type="button" aria-pressed={kind === 'FnB'} onClick={() => setKind('FnB')}>
              🍜 Ăn uống
            </button>
            <button type="button" aria-pressed={kind === 'Retail'} onClick={() => setKind('Retail')}>
              🛒 Bán lẻ
            </button>
          </div>
        </div>

        <div className={`card${kindChanged ? ' warn-card' : ''}`}>
          <div className="pay-row">
            <span>Tỷ lệ thuế trên doanh thu</span>
            <b className="num">{rate}</b>
          </div>
          <p className="note">
            {kind === 'FnB'
              ? 'Ăn uống, dịch vụ có nguyên vật liệu: 3% GTGT + 1,5% TNCN.'
              : 'Bán lẻ hàng hoá: 1% GTGT + 0,5% TNCN.'}
            {kindChanged && ' Đổi ngành sẽ đổi luôn tỷ lệ thuế cho các quý tính từ giờ.'}
          </p>
        </div>
      </div>

      <div className="dock">
        <button type="button" className="btn btn-brand btn-lg grow" onClick={save} disabled={busy}>
          {busy ? 'Đang lưu…' : 'Lưu'}
        </button>
      </div>
    </div>
  )
}
